import { useMemo, useState } from 'react'
import {
  CloseButton,
  Button,
  Checkbox,
  HStack,
  Input,
  Text,
  useDisclosure,
  Dialog,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiPlus } from 'react-icons/fi'
import { useIsMobile } from '../../../hooks/useIsMobile'
import { useLegislators, useLobbyDayEvent } from '../../../hooks/useLobbyDay'
import { useAddTarget, useTargetCoverage } from '../../../hooks/useLobbyRecruit'
import { ResponsiveModal, ResponsiveTable, type ResponsiveColumn } from '../../shared'
import type { LegislatorDto } from '../../../types/lobbyDay'
import { chamberDistrictLabel } from './recruitCoverage'
import { CHAMBER_FILTER_ANY, chamberFilterParams, levelFilterValue } from '../chamberFilter'
import { ChamberFilterSelect } from '../ChamberFilterSelect'

interface AddTargetPickerProps {
  eventId: string
}

export function AddTargetPicker({ eventId }: AddTargetPickerProps) {
  const disclosure = useDisclosure()
  const isMobile = useIsMobile()
  const [search, setSearch] = useState('')
  const [chamberFilter, setChamberFilter] = useState(CHAMBER_FILTER_ANY)
  const [isPriority, setIsPriority] = useState(false)
  const [addingId, setAddingId] = useState<string | undefined>(undefined)

  const { data: event } = useLobbyDayEvent(eventId)
  // Hard-scope to the event's jurisdiction: default the chamber filter to the event's own level
  // (State event -> state legislators only, Federal -> federal only). Deriving the effective value
  // rather than seeding on open means an event that loads AFTER the modal opens still narrows
  // correctly instead of briefly showing every chamber.
  const levelDefault = event?.level ? levelFilterValue(event.level) : CHAMBER_FILTER_ANY
  const effectiveChamberFilter = chamberFilter || levelDefault
  const { chamber, level } = chamberFilterParams(effectiveChamberFilter)

  const { data: legislators, isLoading } = useLegislators({
    search: search || undefined,
    chamber,
    level,
    state: event?.state ?? undefined,
  })
  const { data: coverage } = useTargetCoverage(eventId)
  const addTarget = useAddTarget(eventId)

  const existingLegislatorIds = useMemo(
    () => new Set((coverage ?? []).map((row) => row.legislatorId)),
    [coverage],
  )

  const rows = useMemo(
    () => (legislators ?? []).filter((leg) => !existingLegislatorIds.has(leg.id)),
    [legislators, existingLegislatorIds],
  )

  const handleClose = () => {
    setSearch('')
    setChamberFilter(CHAMBER_FILTER_ANY)
    setIsPriority(false)
    setAddingId(undefined)
    disclosure.onClose()
  }

  const handleAdd = async (legislator: LegislatorDto) => {
    setAddingId(legislator.id)
    try {
      await addTarget.mutateAsync({ legislatorId: legislator.id, isPriority })
      toaster.create({ type: 'success', title: `${legislator.fullName} added to this event` })
    } catch {
      toaster.create({ type: 'error', title: 'Failed to add target' })
    } finally {
      setAddingId(undefined)
    }
  }

  const columns: ResponsiveColumn<LegislatorDto>[] = [
    {
      header: 'Name',
      mobileRole: 'title',
      cell: (leg) => leg.fullName,
    },
    {
      header: 'Chamber',
      mobileRole: 'meta',
      cell: (leg) => chamberDistrictLabel(leg.chamber, leg.district),
    },
    {
      header: 'Party',
      mobileRole: 'badge',
      cell: (leg) => leg.party ?? '—',
    },
  ]

  return (
    <>
      <Button
        colorPalette="brand"
        size="sm"
        minH={isMobile ? '44px' : undefined}
        onClick={disclosure.onOpen}><FiPlus />Add legislators
              </Button>

      <ResponsiveModal isOpen={disclosure.open} onClose={handleClose} size="lg">
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Add legislators to this event</Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
            <Dialog.Body>
              <HStack mb={3} gap={3} flexWrap="wrap">
                <Input
                  placeholder="Search legislators by name…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                  flex={1}
                  minW="200px"
                />
                <ChamberFilterSelect
                  value={effectiveChamberFilter}
                  onChange={setChamberFilter}
                  width="180px"
                  level={event?.level ?? undefined}
                />
              </HStack>

              <Checkbox.Root
                mb={3}
                minH="44px"
                onCheckedChange={(e) => setIsPriority(e.checked === true)}
                checked={isPriority}
              ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>Mark as priority
                              </Checkbox.Label></Checkbox.Root>

              {rows.length === 0 && !isLoading && (
                <Text color="gray.500" fontSize="sm" textAlign="center" py={6}>
                  No legislators match this event's jurisdiction. Add them from the Legislators page, or
                  broaden the chamber filter above.
                </Text>
              )}

              {(rows.length > 0 || isLoading) && (
                <ResponsiveTable
                  bare
                  columns={columns}
                  rows={rows}
                  keyFn={(leg) => leg.id}
                  isLoading={isLoading}
                  onRowClick={(leg) => { if (!addingId) handleAdd(leg) }}
                  emptyMessage="No legislators found."
                />
              )}
              {addingId && (
                <Text fontSize="xs" color="gray.500" mt={2}>Adding…</Text>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" onClick={handleClose}>Done</Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>
    </>
  );
}
